import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import type { ErrorModel } from '../api/types.gen'

type StorableError = Error | ErrorModel

/**
 * Stores errors that occur during navigation or data loading
 * This allows displaying error messages on the UI
 */
export const useErrorsStore = defineStore('errors', () => {
  const errors = shallowRef<StorableError[]>([])
  function pushError(error: StorableError) {
    errors.value.push(error)
  }
  function clearErrors() {
    errors.value = []
  }

  const lastError = computed(() => errors.value[errors.value.length - 1] || null)

  const formattedErr = computed(() => {
    if (!lastError.value)
      return null

    if (isErrorModel(lastError.value)) {
      return {
        title: lastError.value.title,
        message: lastError.value.detail,
        code: 'E_UNKNOWN' // could probably covert this based on status?
      }
    }

    // Generic Error
    return {
      title: lastError.value.name || 'Error',
      message: lastError.value.message || 'An error occurred.',
      code: 'E_UNKNOWN' as const,
    }
  })

  return { errors, lastError, formattedErr, pushError, clearErrors }
})

function isErrorModel(err: StorableError): err is ErrorModel {
  return 'title' in err && 'detail' in err && 'status' in err
}